import React, { Fragment, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import compose from 'lodash.flowright';

import { mapProps } from '../../../../util/hocs';
import Button from '../../../../components/global/Button';
import DownloadPhotoButtonContainer from '../../../../containers/global/DownloadPhotoButtonContainer';
import { IMAGE_SIZE_CODE } from '../../../../constants/image-size-codes';
import {
  ANALYTIC_EVENTS,
  trackWebGalleryEvent,
} from '../../utilities/analytics';
import useBasename from '../../../../components/routing/useBasename';
import { canAccessOriginalDownloadsOnBasename } from '../../../../util/product-permissions';
import useContributorPermission from '../../../pro/services/contributors/hooks/useContributorPermission';
import {
  PERMISSION_TYPES,
  CONTRIBUTOR_VIEW_ACCESS,
} from '../../../../constants/contributors';
import Badge from '../../../../components/global/Badge';
import CustomControl from '../../../../components/global/CustomControl';
import { formatPriceForDisplay } from '../../../../util/format-money';
import {
  allowsDownloadWatermarkOption,
  allowsFreeDownloads,
  allowsPhotoPurchase,
} from '../../../../util/album';
import { isPhotoDownloadPurchased } from '../../utilities/web-gallery-cart-utils';
import useOpenModal from '../../../../hooks/useOpenModal';
import withModal from '../../../../components/global/modal/withModal';
import InfoCircleIcon from '../../../../components/icons/InfoCircleIcon';
import { CHECKOUT_CART_MODAL } from '../../../../services/checkout/components/CheckoutCart/modal';
import {
  getCheckoutCart,
  getCheckoutCartTotalItems,
  getCheckoutCartTotalPrice,
} from '../../../../services/checkout/redux/checkoutSelectors';
import { getPhotoDownloadCartItem } from '../../../../services/checkout/redux/helpers';
import { togglePhotoDownloadCartItem } from '../../../../services/checkout/redux/checkoutActions';

const propTypes = {
  photo: PropTypes.shape({
    id: PropTypes.string,
  }),
  album: PropTypes.shape({
    id: PropTypes.string,
  }),
  forceAllowHiResDownloads: PropTypes.bool,
  onRequestClose: PropTypes.func,
};

const WebGalleryDownloadModal = ({
  photo,
  album,
  forceAllowHiResDownloads,
  onRequestClose,
}) => {
  const openModal = useOpenModal();
  const { cart, totalCartItems, finalPrice } = useSelector(state => ({
    cart: getCheckoutCart(state),
    totalCartItems: getCheckoutCartTotalItems(state),
    finalPrice: getCheckoutCartTotalPrice(state),
  }));
  const dispatch = useDispatch();
  const isDownloadInCart = getPhotoDownloadCartItem(cart, {
    photoId: photo?.id,
    albumId: album?.id,
  });

  // This is a temporary cart - only once the user hits "add to cart"
  // Do we update the actual cart.
  const [localCart, setLocalCart] = useState({
    mediumResDownload: false,
    highResDownload: isDownloadInCart,
  });

  const [downloadWithWatermark, setDownloadWithWatermark] = useState(true);
  const basename = useBasename();
  const { contributorHierarchyRecord } = useContributorPermission({
    entityId: album?.id,
    permissions: [
      {
        type: PERMISSION_TYPES.VIEW_ACCESS,
        permission: CONTRIBUTOR_VIEW_ACCESS.ALL,
      },
    ],
  });
  const allowOriginalDownload =
    canAccessOriginalDownloadsOnBasename(basename) ||
    !!contributorHierarchyRecord;
  const hiResDownloadPrice = album?.web_gallery_price_per_photo ?? null;
  const onSelectDownload = useCallback(
    () =>
      dispatch(
        togglePhotoDownloadCartItem({
          photo,
          album,
          webGalleryPrice: hiResDownloadPrice,
        }),
      ),
    [dispatch],
  );

  const canPurchaseHiResPhoto = allowsPhotoPurchase(album);
  const canDownloadHiResPhoto =
    forceAllowHiResDownloads ||
    isPhotoDownloadPurchased(album, photo) ||
    allowsFreeDownloads(album);

  return (
    <div className="p-1">
      <h3 className="text-center mb-3">
        {canDownloadHiResPhoto ? 'Photo Download' : 'Pick a resolution'}
      </h3>
      {!canDownloadHiResPhoto && (
        <div className="d-flex align-items-center mt-1">
          <CustomControl
            type="checkbox"
            label="Medium-Res"
            id={`dl-medium-res-${photo?.id}`}
            checked={localCart.mediumResDownload}
            onChange={() => {
              setLocalCart({
                ...localCart,
                mediumResDownload: !localCart.mediumResDownload,
              });
            }}
          />
          <span className="ml-auto text-yellow text-extra-bold">
            <Badge badgeStyle="yellow" className="text-extra-bold">
              FREE
            </Badge>
          </span>
        </div>
      )}
      {canPurchaseHiResPhoto && !canDownloadHiResPhoto && (
        <div className="d-flex align-items-center mt-1">
          <CustomControl
            type="checkbox"
            label="Hi-Res"
            id={`dl-hi-res-${photo?.id}`}
            disabled={isDownloadInCart}
            checked={isDownloadInCart || localCart.highResDownload}
            onChange={() => {
              setLocalCart({
                ...localCart,
                highResDownload: !localCart.highResDownload,
              });
            }}
          />
          <Badge badgeStyle="yellow" className="ml-auto text-extra-bold">
            {hiResDownloadPrice
              ? `$${formatPriceForDisplay(hiResDownloadPrice.value)}`
              : 'Free'}
          </Badge>
        </div>
      )}

      {allowsDownloadWatermarkOption(album) && (
        <CustomControl
          type="checkbox"
          label="Include Logo"
          id={`dl-branding-${photo?.id}`}
          checked={downloadWithWatermark}
          onChange={() => {
            setDownloadWithWatermark(!downloadWithWatermark);
          }}
          size="sm"
          className="mt-2"
        />
      )}
      {localCart.mediumResDownload && !canDownloadHiResPhoto && (
        <Fragment>
          <p className="text-xs text-light text-center mt-2 mb-0">
            <InfoCircleIcon size={12} className="v-align-middle" /> Medium
            resolution photos are not downloadable in bulk and must be saved
            individually.
          </p>
          <DownloadPhotoButtonContainer
            photoId={photo?.id}
            photoSize={IMAGE_SIZE_CODE.MEDIUM2X}
            includeWatermark={downloadWithWatermark}
            btnBlock
            btnOutline
            className="d-flex mt-2"
            onDownload={() => {
              setLocalCart({
                ...localCart,
                mediumResDownload: false,
              });

              trackWebGalleryEvent(ANALYTIC_EVENTS.PHOTOS_DOWNLOADED, {
                photo_id: photo?.id,
              });
            }}
          >
            <span>Download Medium-Res</span>
          </DownloadPhotoButtonContainer>
        </Fragment>
      )}

      {canDownloadHiResPhoto && (
        <DownloadPhotoButtonContainer
          photoId={photo?.id}
          photoSize={IMAGE_SIZE_CODE.LARGE2X}
          includeWatermark={downloadWithWatermark}
          btnBlock
          className="mt-2"
          onDownload={() => {
            trackWebGalleryEvent(ANALYTIC_EVENTS.PHOTOS_DOWNLOADED, {
              photo_id: photo?.id,
            });
          }}
        >
          <span>Download Hi-Res</span>
        </DownloadPhotoButtonContainer>
      )}

      {allowOriginalDownload && (
        <DownloadPhotoButtonContainer
          photoId={photo?.id}
          photoSize={IMAGE_SIZE_CODE.LARGE2X}
          includeWatermark={downloadWithWatermark}
          downloadOriginal
          btnBlock
          className="mt-2"
          onDownload={() => {
            trackWebGalleryEvent(ANALYTIC_EVENTS.PHOTOS_DOWNLOADED, {
              photo_id: photo?.id,
            });
          }}
        >
          <span>Download Original</span>
        </DownloadPhotoButtonContainer>
      )}
      {localCart.highResDownload && (
        <Button
          btnBlock
          className="mt-2"
          disabled={isDownloadInCart}
          btnOutline={isDownloadInCart}
          onClick={() => {
            onSelectDownload();
          }}
        >
          <div className="d-flex align-items-center justify-content-center">
            <span>
              {isDownloadInCart ? 'Added to Cart' : 'Add to Cart'}
              {totalCartItems > 0 ? ` (${totalCartItems})` : ''}
            </span>
          </div>
        </Button>
      )}
      {totalCartItems > 0 && (
        <Button
          btnBlock
          className="mt-2"
          onClick={() => {
            openModal(CHECKOUT_CART_MODAL);
            onRequestClose();
          }}
        >
          Checkout Now (${formatPriceForDisplay(finalPrice.value)})
        </Button>
      )}
      <Button
        btnBlock
        btnStyle="link"
        onClick={onRequestClose}
        className="text-sm text-center text-bold mt-3 mb-1"
      >
        Dismiss
      </Button>
    </div>
  );
};

WebGalleryDownloadModal.propTypes = propTypes;

export const WEB_GALLERY_DOWNLOAD_MODAL = 'modal/WEB_GALLERY_DOWNLOAD_MODAL';

export default withModal(
  compose(
    mapProps(({ actionProps, ...props }) => ({
      photo: actionProps?.photo,
      album: actionProps?.album,
      forceAllowHiResDownloads: actionProps?.forceAllowHiResDownloads,
      ...props,
    })),
  )(WebGalleryDownloadModal),
  {
    name: WEB_GALLERY_DOWNLOAD_MODAL,
    hideHeader: true,
    size: 'xs',
  },
);
